// Lexical JSON parser — extracts translatable segments from doc-center template bodies

const VARIABLE_NODE_TYPES = new Set(["textVarNode", "calculatedVarNode"]);
interface LexicalNode {
  type: string;
  children?: LexicalNode[];
  text?: string;
  tag?: string;
  direction?: string;
  format?: string | number;
  indent?: number;
  version?: number;
  textFormat?: number;
  textStyle?: string;
  listType?: string;
  start?: number;
  value?: number;
  placeHolder?: string;
  varId?: string;
}
interface LexicalRoot {
  root: {
    children: LexicalNode[];
    direction?: string;
    format?: string;
    indent?: number;
    type?: string;
    version?: number;
  };
}
export interface ParsedSegment {
  id: string;
  type: "heading" | "paragraph" | "list";
  tag?: string;
  text: string;
  structuralPath: string;
  isTranslatable: boolean;
  /** Index into the translatable-only array — used for injection alignment */
  injectionIndex: number;
  originalNode: LexicalNode;
}
function isVariableNode(node: LexicalNode): boolean {
  return VARIABLE_NODE_TYPES.has(node.type);
}
function extractText(node: LexicalNode): string {
  if (node.type === "text" && node.text) return node.text;
  if (node.children?.length) return node.children.map(extractText).join("");
  return "";
}
function groupHasTranslatableText(nodes: LexicalNode[]): boolean {
  for (const node of nodes) {
    if (node.type === "text" && (node.text || "").trim()) return true;
    if (node.type !== "text" && node.type !== "linebreak") {
      if (collectTextNodes(node).some(t => (t.text || "").trim())) return true;
    }
  }
  return false;
}
function bufferTextContent(nodes: LexicalNode[]): string {
  let text = "";
  for (const node of nodes) {
    if (node.type === "text") text += node.text || "";else if (node.type === "linebreak") text += "\n";else text += extractText(node);
  }
  return text;
}

/**
 * Walk inline children of a paragraph/heading/listitem.
 * Merges consecutive text runs (bold/italic are format flags, not section breaks).
 * Splits when an inline variable appears so injection preserves variable nodes.
 * Whitespace-only runs beside variables attach to the next text group.
 */
function collectInlineSegments(parent: LexicalNode, parentPath: string, segments: ParsedSegment[], counter: {
  index: number;
  injectionIndex: number;
}, type: ParsedSegment["type"], tag?: string): void {
  if (!parent.children?.length) return;
  let bufferNodes: LexicalNode[] = [];
  let pendingLeading: LexicalNode[] = [];
  let partIndex = 0;
  const flush = () => {
    if (!bufferNodes.length) return;
    const buffer = bufferTextContent(bufferNodes);
    const trimmed = buffer.replace(/\s+/g, " ").trim();
    if (!trimmed) {
      pendingLeading.push(...bufferNodes);
      bufferNodes = [];
      return;
    }
    const path = `${parentPath}:part${partIndex}`;
    const nodes = [...pendingLeading, ...bufferNodes];
    pendingLeading = [];
    segments.push({
      id: `seg-${counter.index}`,
      type,
      tag,
      text: trimmed,
      structuralPath: path,
      isTranslatable: true,
      injectionIndex: counter.injectionIndex++,
      originalNode: nodes[0] || parent
    });
    counter.index++;
    partIndex++;
    bufferNodes = [];
  };
  for (const child of parent.children) {
    if (child.type === "linebreak") {
      bufferNodes.push(child);
      continue;
    }
    if (child.type === "text") {
      bufferNodes.push(child);
      continue;
    }
    if (isVariableNode(child)) {
      flush();
      partIndex++;
      continue;
    }
    const nested = extractText(child);
    if (nested) {
      bufferNodes.push(child);
    }
  }
  flush();
  if (pendingLeading.length && segments.length) {
    const last = segments[segments.length - 1];
    last.originalNode = pendingLeading[0] || last.originalNode;
  }
}
function collectTextSegments(nodes: LexicalNode[], segments: ParsedSegment[], counter: {
  index: number;
  injectionIndex: number;
}, pathPrefix = "root"): void {
  nodes.forEach((node, nodeIndex) => {
    const nodePath = `${pathPrefix}/${node.type}[${nodeIndex}]`;
    if (node.type === "heading") {
      collectInlineSegments(node, nodePath, segments, counter, "heading", node.tag || "h3");
      return;
    }
    if (node.type === "paragraph" || node.type === "listitem") {
      const type = node.type === "listitem" ? "list" : "paragraph";
      collectInlineSegments(node, nodePath, segments, counter, type);
      return;
    }
    if (node.type === "list" && node.children?.length) {
      collectTextSegments(node.children, segments, counter, nodePath);
      return;
    }
    if (node.children?.length) {
      const nonTextChildren = node.children.filter(c => c.type !== "text" && c.type !== "linebreak");
      if (nonTextChildren.length > 0) {
        collectTextSegments(nonTextChildren, segments, counter, nodePath);
      }
    }
  });
}
export function parseLexicalBody(bodyString: string): ParsedSegment[] {
  let parsed: LexicalRoot;
  try {
    parsed = JSON.parse(bodyString);
  } catch (e) {
    console.error("Failed to parse Lexical body:", e);
    return [];
  }
  if (!parsed?.root?.children) return [];
  const segments: ParsedSegment[] = [];
  const counter = {
    index: 0,
    injectionIndex: 0
  };
  collectTextSegments(parsed.root.children, segments, counter);
  return segments;
}
export function matchTranslations(enSegments: ParsedSegment[], frBody: string | null): Record<number, string> {
  if (!frBody) return {};
  const frSegments = parseLexicalBody(frBody).filter(s => s.isTranslatable);
  const enTranslatable = enSegments.filter(s => s.isTranslatable);
  const matches: Record<number, string> = {};
  enTranslatable.forEach((_, index) => {
    if (frSegments[index]) matches[index] = frSegments[index].text;
  });
  return matches;
}
function collectTextNodes(node: LexicalNode): LexicalNode[] {
  const results: LexicalNode[] = [];
  if (node.type === "text") results.push(node);
  if (node.children) {
    for (const child of node.children) {
      results.push(...collectTextNodes(child));
    }
  }
  return results;
}
function replaceGroupTextContent(group: LexicalNode[], newText: string): void {
  const textNodes: LexicalNode[] = [];
  for (const node of group) {
    textNodes.push(...collectTextNodes(node));
  }
  if (!textNodes.length) return;
  const fullOriginal = textNodes.map(n => n.text || "").join("");
  const leading = fullOriginal.match(/^\s*/)?.[0] || "";
  const trailing = fullOriginal.match(/\s*$/)?.[0] || "";
  textNodes[0].text = leading + newText.trim() + trailing;
  for (let i = 1; i < textNodes.length; i++) textNodes[i].text = "";
}
function collectInjectionGroups(nodes: LexicalNode[], results: LexicalNode[][], pathPrefix = "root"): void {
  nodes.forEach((node, nodeIndex) => {
    const nodePath = `${pathPrefix}/${node.type}[${nodeIndex}]`;
    if (node.type === "heading") {
      collectInlineInjectionGroups(node, nodePath, results);
      return;
    }
    if (node.type === "paragraph" || node.type === "listitem") {
      collectInlineInjectionGroups(node, nodePath, results);
      return;
    }
    if (node.type === "list" && node.children?.length) {
      collectInjectionGroups(node.children, results, nodePath);
      return;
    }
    if (node.children?.length) {
      const nonTextChildren = node.children.filter(c => c.type !== "text" && c.type !== "linebreak");
      if (nonTextChildren.length) {
        collectInjectionGroups(nonTextChildren, results, nodePath);
      }
    }
  });
}
function collectInlineInjectionGroups(parent: LexicalNode, parentPath: string, results: LexicalNode[][]): void {
  if (!parent.children?.length) return;
  let bufferNodes: LexicalNode[] = [];
  let pendingLeading: LexicalNode[] = [];
  const flush = () => {
    if (!bufferNodes.length) return;
    if (!groupHasTranslatableText(bufferNodes)) {
      pendingLeading.push(...bufferNodes);
      bufferNodes = [];
      return;
    }
    results.push([...pendingLeading, ...bufferNodes]);
    pendingLeading = [];
    bufferNodes = [];
  };
  for (const child of parent.children) {
    if (child.type === "linebreak") {
      bufferNodes.push(child);
      continue;
    }
    if (child.type === "text") {
      bufferNodes.push(child);
      continue;
    }
    if (isVariableNode(child)) {
      flush();
      continue;
    }
    const nested = collectTextNodes(child);
    if (nested.length) bufferNodes.push(...nested);
  }
  flush();
  if (pendingLeading.length && results.length) {
    results[results.length - 1].push(...pendingLeading);
  }
}
export function injectTranslationsIntoLexical(bodyString: string, translatedTexts: (string | undefined)[]): string {
  let parsed: LexicalRoot;
  try {
    parsed = JSON.parse(bodyString);
  } catch (e) {
    console.error("Failed to parse Lexical body for injection:", e);
    return bodyString;
  }
  if (!parsed?.root?.children) return bodyString;
  const textGroups: LexicalNode[][] = [];
  collectInjectionGroups(parsed.root.children, textGroups);
  if (translatedTexts.length !== textGroups.length) {
    console.warn(`[lexicalParser] Segment / text-group count mismatch — ${translatedTexts.length} translations provided, but the target body has ${textGroups.length} text-bearing groups.`);
  }
  textGroups.forEach((group, index) => {
    const translatedText = translatedTexts[index];
    if (translatedText) replaceGroupTextContent(group, translatedText);
  });
  return JSON.stringify(parsed, null, 2);
}

/**
 * Build the translatedTexts array from segments (skips removed / non-translatable).
 */
export function segmentsToInjectionTexts(segments: {
  status: string;
  mergedText?: string;
}[]): (string | undefined)[] {
  return segments.filter(s => s.status !== "removed").map(s => s.mergedText);
}